import type { QuerySpec } from '@helios/powersync-client-core';
import { getSystemFieldByKey } from './form-field-registry';

// ─── Row Types ───

export interface FormDefinitionRow {
  id: string;
  org_id: string;
  clinic_id: string | null;
  name: string;
  description: string | null;
  status: string; // 'draft' | 'published' | 'archived'
  version: number;
  fields_schema: string; // JSON text on client
  default_view: string; // 'render' | 'form'
  /** Workflow purpose designation: null (general), 'registration', etc. */
  purpose: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

// ─── Form Purpose ───

export * from './form-purpose.js';
import type { FormPurpose } from './form-purpose.js';

export interface FormTemplatePageRow {
  id: string;
  form_definition_id: string;
  page_number: number;
  image_url: string;
  image_width: number;
  image_height: number;
  label: string | null;
  created_at: string;
  updated_at: string;
}

export interface FormTemplateMappingRow {
  id: string;
  form_definition_id: string;
  page_id: string;
  field_key: string;
  field_type: string; // 'binding' | 'drawing' | 'text' | 'checkbox' | 'signature'
  x_pct: number;
  y_pct: number;
  w_pct: number;
  h_pct: number;
  config: string; // JSON text on client
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── Field Types ───

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'select'
  | 'multi-select'
  | 'yes-no'
  | 'binding' // Legacy — kept for backward compat; new forms should use binding metadata instead
  | 'drawing'
  | 'signature'
  // PRD.322 — static/consent wording. Content lives in `config`, so no schema
  // change was needed and PRD.321 clones it through `fields_schema` already.
  | 'rich-text';

export interface FormFieldOption {
  id: string;
  label: string;
  value: string;
}

// ─── Binding Model V2 ───

export type FormFieldBindingMode = 'read_only' | 'default_only' | 'two_way';

export type FormFieldBindingSource = 'patient' | 'encounter' | 'form_context';

export type DomainGroup =
  | 'demographics'
  | 'contact'
  | 'identifiers'
  | 'emergency_contact'
  | 'medication'
  | 'allergies'
  | 'notes_alerts'
  | 'encounter';

export interface FormFieldBindingConfig {
  mode: FormFieldBindingMode;
  source: FormFieldBindingSource;
  bindingKey: string;
  domainGroup?: DomainGroup;
}

// ─── Generated Field Config ───

export type GeneratedFieldStrategy = 'auto_increment';

export interface GeneratedFieldConfig {
  /** Generation strategy */
  strategy: GeneratedFieldStrategy;
  /**
   * Pattern template with `{placeholder}` references.
   * Supported placeholders:
   *   {auto_increment} — next integer from existing data
   *   {year}           — current 4-digit year
   *   {month}          — current 2-digit month
   *   {day}            — current 2-digit day
   *
   * Examples: "{auto_increment}", "P-{auto_increment}", "{year}-{auto_increment}"
   */
  pattern: string;
  /**
   * Minimum width for the auto_increment portion (zero-padded).
   * E.g. padWidth=5 → "00042". Default: no padding.
   */
  padWidth?: number;
}

export interface FormFieldDef {
  key: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  helpText?: string;
  layout?: 'half' | 'full';
  options?: FormFieldOption[];
  /** Validation rules (same schema as @helios/validation Rule[]) */
  validation?: unknown[];
  /** Binding Model V2 — optional binding metadata for any field type */
  binding?: FormFieldBindingConfig;
  /**
   * Domain group for grouped rendering and save orchestration.
   * Available on any field (bound or unbound). When set, this is the
   * authoritative group for this field in this form schema.
   */
  domainGroup?: DomainGroup;
  /** Generated field config — when set, the value is auto-generated at form render time */
  generated?: GeneratedFieldConfig;
  config?: BindingConfig | DrawingConfig | RichTextConfig;
}

/**
 * @deprecated Legacy binding config. Use FormFieldBindingConfig (binding metadata on field) instead.
 * Kept for backward compatibility with schemas that use `type: 'binding'`.
 */
export interface BindingConfig {
  source: string; // 'patient' | 'encounter'
  path: string; // e.g. 'full_name', 'birth_date'
}

export interface DrawingConfig {
  placeholder?: string;
}

/** PRD.322 — Rich Text field configuration. */
export interface RichTextConfig {
  /** Serialized `RichTextDoc` — the definition's default content. */
  content?: string;
  /**
   * When true the content becomes an answer: it is snapshotted into
   * `form_instance_values` on first fill and edited per patient. When false
   * (the default) it is display-only wording and never an answer field.
   */
  editableWhenFilling?: boolean;
}

// ─── Legacy Binding Normalization ───

/**
 * Resolve widget type and domain group for a legacy binding field via the
 * system field registry. Falls back to sensible defaults for unknown keys.
 */
function resolveRegistryDefaults(fullKey: string): {
  widgetType: FormFieldType;
  domainGroup?: DomainGroup;
} {
  const entry = getSystemFieldByKey(fullKey);
  if (entry) return { widgetType: entry.defaultWidgetType, domainGroup: entry.domainGroup };
  // Fallback for keys not yet in the registry
  return { widgetType: 'text', domainGroup: undefined };
}

/**
 * Normalize a single field: if it uses the legacy `type: 'binding'` pattern,
 * convert it to a proper widget type + binding metadata using registry lookups.
 * Returns the field unchanged if it is not a legacy binding field.
 */
export function normalizeLegacyBindingField(field: FormFieldDef): FormFieldDef {
  if (field.type !== 'binding') return field;
  // Already has V2 binding metadata — just fix the type via registry
  if (field.binding) {
    const fullKey = `${field.binding.source}.${field.binding.bindingKey}`;
    const { widgetType } = resolveRegistryDefaults(fullKey);
    return { ...field, type: widgetType };
  }
  const cfg = field.config as BindingConfig | undefined;
  if (!cfg) {
    // Binding field with no config — treat as plain text, read_only
    return {
      ...field,
      type: 'text',
      binding: { mode: 'read_only', source: 'patient', bindingKey: '' },
      config: undefined,
    };
  }
  const source = (cfg.source === 'encounter' ? 'encounter' : 'patient') as FormFieldBindingSource;
  const bindingKey = cfg.path;
  const fullKey = `${source}.${bindingKey}`;
  const { widgetType, domainGroup } = resolveRegistryDefaults(fullKey);
  return {
    ...field,
    type: widgetType,
    binding: {
      mode: 'read_only', // Legacy binding was always read-only
      source,
      bindingKey,
      domainGroup,
    },
    // Keep legacy config for serialization compatibility
  };
}

/**
 * Normalize all fields in a V1 schema, converting legacy binding fields
 * to the V2 binding model at runtime.
 */
export function normalizeSchemaBindings(schema: FormFieldsSchemaV1): FormFieldsSchemaV1 {
  return {
    ...schema,
    sections: schema.sections.map((section) => ({
      ...section,
      fields: section.fields.map(normalizeLegacyBindingField),
    })),
  };
}

/**
 * Check if a field has binding (either V2 metadata or legacy type).
 */
export function fieldHasBinding(field: FormFieldDef): boolean {
  return !!field.binding || field.type === 'binding';
}

/**
 * Resolve the binding key string (e.g. 'patient.full_name') from a field.
 * Works with both V2 binding metadata and legacy config.
 */
export function resolveBindingKey(field: FormFieldDef): string | null {
  if (field.binding) {
    return `${field.binding.source}.${field.binding.bindingKey}`;
  }
  if (field.type === 'binding' && field.config) {
    const cfg = field.config as BindingConfig;
    return `${cfg.source}.${cfg.path}`;
  }
  return null;
}

/**
 * Determine if a field should be editable based on its binding mode.
 * Unbound fields are always editable (subject to form-level readOnly).
 */
export function isFieldEditable(field: FormFieldDef): boolean {
  if (!field.binding) return true; // Unbound — editable
  return field.binding.mode !== 'read_only';
}

// ─── Domain Group Resolution & Filtering ───

/**
 * Resolve the effective domain group for a field using this precedence:
 * 1. Top-level field domainGroup (explicit schema-level assignment, works for any field)
 * 2. Binding-level domainGroup (legacy location, still supported)
 * 3. Registry default domain group for bound system fields
 * 4. undefined (no group assigned)
 */
export function getEffectiveDomainGroup(field: FormFieldDef): DomainGroup | undefined {
  // 1. Top-level field group (authoritative for this form)
  if (field.domainGroup) return field.domainGroup;
  // 2. Binding-level group (backward compat)
  if (field.binding?.domainGroup) return field.binding.domainGroup;
  // 3. Registry default for bound fields
  if (field.binding) {
    const fullKey = `${field.binding.source}.${field.binding.bindingKey}`;
    const entry = getSystemFieldByKey(fullKey);
    if (entry) return entry.domainGroup;
  }
  // 4. No group
  return undefined;
}

/**
 * Filter a full form schema to include only fields whose effective domain group
 * matches one of the requested groups. Returns a derived view — the authoritative
 * schema is not mutated.
 *
 * - Preserves original section order and field order
 * - Drops sections that have no matching fields after filtering
 * - Returns the full schema unchanged if `groups` is empty or undefined
 */
export function filterSchemaByDomainGroups(
  schema: FormFieldsSchemaV1,
  groups: DomainGroup[] | undefined,
): FormFieldsSchemaV1 {
  if (!groups || groups.length === 0) return schema;
  const groupSet = new Set(groups);
  const filteredSections = schema.sections
    .map((section) => ({
      ...section,
      fields: section.fields.filter((field) => {
        const effective = getEffectiveDomainGroup(field);
        return effective !== undefined && groupSet.has(effective);
      }),
    }))
    .filter((section) => section.fields.length > 0);
  return { ...schema, sections: filteredSections };
}

/**
 * Collect all distinct effective domain groups present in a schema.
 * Useful for introspection (e.g. "which groups does this form cover?").
 */
export function getSchemaGroups(schema: FormFieldsSchemaV1): DomainGroup[] {
  const groups = new Set<DomainGroup>();
  for (const section of schema.sections) {
    for (const field of section.fields) {
      const g = getEffectiveDomainGroup(field);
      if (g) groups.add(g);
    }
  }
  return [...groups];
}

// ─── Queries ───

/**
 * List all form definitions for the org, ordered by updated_at desc.
 */
export function qFormDefinitions(orgId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'definitions', orgId] as const,
    sql: `SELECT * FROM form_definitions WHERE org_id = ? ORDER BY updated_at DESC`,
    params: [orgId],
    watch: ['form_definitions'],
  };
}

/**
 * Fetch a single form definition by ID.
 */
export function qFormDefinitionById(id: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'definition', id] as const,
    sql: `SELECT * FROM form_definitions WHERE id = ?`,
    params: [id],
    watch: ['form_definitions'],
  };
}

/**
 * List published form definitions for the org.
 */
export function qPublishedFormDefinitions(orgId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'definitions', 'published', orgId] as const,
    sql: `SELECT * FROM form_definitions WHERE org_id = ? AND status = 'published' ORDER BY name ASC`,
    params: [orgId],
    watch: ['form_definitions'],
  };
}

/**
 * Fetch the active form definition for a given purpose.
 * Returns at most one row: the published form with the matching purpose for the org.
 */
export function qActiveFormByPurpose(
  orgId: string,
  purpose: FormPurpose,
): QuerySpec<[string, string]> {
  return {
    key: ['forms', 'definitions', 'purpose', purpose, orgId] as const,
    sql: `SELECT * FROM form_definitions WHERE org_id = ? AND purpose = ? AND status = 'published' ORDER BY updated_at DESC LIMIT 1`,
    params: [orgId, purpose],
    watch: ['form_definitions'],
  };
}

/**
 * Fetch template pages for a form definition, ordered by page_number.
 */
export function qFormTemplatePages(formDefinitionId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'pages', formDefinitionId] as const,
    sql: `SELECT * FROM form_template_pages WHERE form_definition_id = ? ORDER BY page_number ASC`,
    params: [formDefinitionId],
    watch: ['form_template_pages'],
  };
}

/**
 * Fetch template mappings for a form definition.
 */
export function qFormTemplateMappings(formDefinitionId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'mappings', formDefinitionId] as const,
    sql: `SELECT * FROM form_template_mappings WHERE form_definition_id = ? ORDER BY sort_order ASC`,
    params: [formDefinitionId],
    watch: ['form_template_mappings'],
  };
}

/**
 * Fetch template mappings for a specific page.
 */
export function qFormTemplateMappingsByPage(pageId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'mappings', 'by-page', pageId] as const,
    sql: `SELECT * FROM form_template_mappings WHERE page_id = ? ORDER BY sort_order ASC`,
    params: [pageId],
    watch: ['form_template_mappings'],
  };
}

// ─── Section-based schema (V1) ───

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  fields: FormFieldDef[];
}

export interface FormFieldsSchemaV1 {
  version: 1;
  sections: FormSection[];
}

// ─── Helpers ───

let _uidCounter = 0;
function uid(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${(++_uidCounter).toString(36)}`;
}

/**
 * Parse fields_schema JSON text. Handles both:
 * - V1 section-based: { version: 1, sections: [...] }
 * - Legacy flat array: FormFieldDef[]
 * Always returns V1 shape.
 *
 * Legacy `type: 'binding'` fields are normalized to V2 binding metadata at parse time.
 */
export function parseFieldsSchemaV1(raw: string | null): FormFieldsSchemaV1 {
  if (!raw) return { version: 1, sections: [] };
  try {
    const parsed = JSON.parse(raw);
    let schema: FormFieldsSchemaV1;
    // V1 section-based
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.version === 1 &&
      Array.isArray(parsed.sections)
    ) {
      schema = parsed as FormFieldsSchemaV1;
    }
    // Legacy flat array — wrap into one section
    else if (Array.isArray(parsed)) {
      schema = {
        version: 1,
        sections: parsed.length > 0 ? [{ id: uid('s'), title: 'Section 1', fields: parsed }] : [],
      };
    } else {
      return { version: 1, sections: [] };
    }
    // Normalize legacy binding fields to V2 model
    return normalizeSchemaBindings(schema);
  } catch {
    return { version: 1, sections: [] };
  }
}

/** Serialize V1 schema to JSON text. */
export function stringifyFieldsSchemaV1(schema: FormFieldsSchemaV1): string {
  return JSON.stringify(schema);
}

/** Flatten all fields from sections into a single array (for Steps 3-6). */
export function flattenFields(schema: FormFieldsSchemaV1): FormFieldDef[] {
  return schema.sections.flatMap((s) => s.fields);
}

/**
 * @deprecated Use parseFieldsSchemaV1 + flattenFields instead.
 * Kept for backward compatibility during migration.
 */
export function parseFieldsSchema(raw: string | null): FormFieldDef[] {
  return flattenFields(parseFieldsSchemaV1(raw));
}

/**
 * @deprecated Use stringifyFieldsSchemaV1 instead.
 */
export function stringifyFieldsSchema(fields: FormFieldDef[]): string {
  return JSON.stringify({ version: 1, sections: [{ id: uid('s'), title: 'Section 1', fields }] });
}
